import React, { useState } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../ui/dialog';
import { Button } from '../ui/button';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Send, Loader2, Scissors, FileText, Wrench, Sparkles, ThumbsUp, ThumbsDown, Check, RefreshCw, ShieldCheck, ShieldAlert } from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const ACTIONS = [
  { value: 'remove',  label: 'Remove from scope',     icon: Scissors, hint: 'Drop this component entirely' },
  { value: 'clarify', label: 'Provide clarification', icon: FileText, hint: 'Answer the question, no scope change' },
  { value: 'other',   label: 'Propose an adjustment', icon: Wrench,   hint: 'Scope change other than removal' },
];

const TONES = [
  { value: 'professional', label: 'Professional' },
  { value: 'warm',         label: 'Warm & consultative' },
  { value: 'concise',      label: 'Direct & concise' },
];

/**
 * Dialog a consultant opens from the Stakeholder Feedback card to promote
 * a single feedback row into a Change Request + email the stakeholder.
 */
export function ResolveFeedbackDialog({
  open, onOpenChange, row,   // row = { component_name, vote, note, from, estimateId, estimateName, feedbackId, sendId, stakeholderEmail, stakeholderName }
  onResolved,
}) {
  const [action, setAction] = useState('clarify');
  const [note, setNote] = useState('');
  const [sendEmail, setSendEmail] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const [tone, setTone] = useState('professional');
  const [rationale, setRationale] = useState('');
  const [peerCount, setPeerCount] = useState(0);
  const [draftId, setDraftId] = useState(null);
  const [rating, setRating] = useState(null);   // 1 | -1 | null
  const [showReason, setShowReason] = useState(false);
  const [ratingReason, setRatingReason] = useState('');
  const [savingRating, setSavingRating] = useState(false);
  const [styleCount, setStyleCount] = useState(0);
  const [styleCritiques, setStyleCritiques] = useState([]);
  const [firmCount, setFirmCount] = useState(0);
  const [compliance, setCompliance] = useState(null);  // { compliant, violations, skipped }
  const [autoRegenerated, setAutoRegenerated] = useState(false);

  // Reset on open
  React.useEffect(() => {
    if (open) {
      setAction(row?.vote === 'drop' ? 'remove' : 'clarify');
      setNote('');
      setSendEmail(true);
      setTone('professional');
      setRationale('');
      setPeerCount(0);
      setDraftId(null);
      setRating(null);
      setShowReason(false);
      setRatingReason('');
      setCompliance(null);
      setAutoRegenerated(false);
      // Fetch the consultant's style memory + firm guardrails count.
      (async () => {
        try {
          const [sm, fg] = await Promise.all([
            axios.get(`${API_URL}/api/change-requests/style-profile`, { withCredentials: true }),
            axios.get(`${API_URL}/api/settings/firm-style-guardrails`, { withCredentials: true }),
          ]);
          setStyleCount(sm.data?.count || 0);
          setStyleCritiques(sm.data?.critiques || []);
          setFirmCount((fg.data?.rules || []).length);
        } catch {
          setStyleCount(0); setStyleCritiques([]); setFirmCount(0);
        }
      })();
    }
  }, [open, row?.vote]);

  if (!row) return null;

  const handleAutoDraft = async ({ previousDraftId = '', critique = '' } = {}) => {
    setDrafting(true);
    setRationale('');
    try {
      const res = await axios.post(`${API_URL}/api/change-requests/auto-draft`, {
        estimate_id: row.estimateId,
        component_name: row.component_name,
        vote: row.vote,
        feedback_note: row.note || '',
        stakeholder_name: row.stakeholderName || '',
        tone,
        previous_draft_id: previousDraftId,
        critique,
      }, { withCredentials: true });
      const d = res.data || {};
      if (d.suggested_action) setAction(d.suggested_action);
      if (d.draft_note) setNote(d.draft_note);
      if (d.rationale) setRationale(d.rationale);
      setPeerCount(d.peer_count || 0);
      setDraftId(d.draft_id || null);
      setCompliance(d.compliance || null);
      setAutoRegenerated(Boolean(d.auto_regenerated));
      setRating(null);
      setShowReason(false);
      setRatingReason('');
      toast.success(
        previousDraftId
          ? 'Regenerated with your feedback'
          : (d.peer_count > 0
              ? `Draft ready — grounded in ${d.peer_count} past proposal${d.peer_count === 1 ? '' : 's'}`
              : 'Draft ready')
      );
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Auto-draft failed');
    } finally {
      setDrafting(false);
    }
  };

  const sendRating = async (value, reason = '') => {
    if (!draftId) return;
    setSavingRating(true);
    try {
      await axios.post(`${API_URL}/api/change-requests/auto-draft/${draftId}/rate`,
        { rating: value, reason }, { withCredentials: true });
      setRating(value);
      setShowReason(false);
      toast.success('Thanks — feedback recorded');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to save rating');
    } finally {
      setSavingRating(false);
    }
  };

  const clearStyleMemory = async () => {
    try {
      await axios.delete(`${API_URL}/api/change-requests/style-profile`,
        { withCredentials: true });
      setStyleCount(0);
      setStyleCritiques([]);
      toast.success('Style memory cleared');
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to clear');
    }
  };

  const handleSubmit = async () => {
    if (!note.trim()) { toast.error('Please write a response'); return; }
    setSubmitting(true);
    try {
      const res = await axios.post(`${API_URL}/api/change-requests`, {
        estimate_id: row.estimateId,
        estimate_name: row.estimateName || '',
        feedback_id: row.feedbackId,
        send_id: row.sendId,
        component_name: row.component_name,
        vote: row.vote,
        stakeholder_email: row.stakeholderEmail,
        stakeholder_name: row.stakeholderName,
        feedback_note: row.note || '',
        proposed_action: action,
        proposed_note: note.trim(),
        send_email: Boolean(sendEmail && row.stakeholderEmail),
      }, { withCredentials: true });
      toast.success(sendEmail ? `Sent to ${row.stakeholderEmail || 'stakeholder'}` : 'Change request saved');
      onResolved?.(res.data?.change_request);
      onOpenChange(false);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to create change request');
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg" data-testid="resolve-feedback-dialog">
        <DialogHeader>
          <DialogTitle className="text-[#0F2040] text-base">
            Resolve feedback &mdash; <span className="text-slate-500 font-normal">{row.component_name}</span>
          </DialogTitle>
          <DialogDescription className="text-xs text-slate-500">
            Propose a response and we&rsquo;ll email{' '}
            <span className="font-medium">{row.stakeholderName || row.stakeholderEmail || 'the stakeholder'}</span>{' '}
            with an Accept/Decline link so the loop closes fast.
          </DialogDescription>
        </DialogHeader>

        {row.note && (
          <div className="bg-slate-50 border border-slate-200 rounded-md p-3 text-[12px] text-slate-700">
            <div className="text-[10px] text-slate-500 uppercase tracking-wide mb-1">Their feedback</div>
            <div className="italic">&ldquo;{row.note}&rdquo;</div>
            <div className="text-[10px] text-slate-400 mt-1">{row.from || row.stakeholderEmail}</div>
          </div>
        )}

        {/* AI Auto-draft row */}
        <div className="flex items-center gap-2 bg-gradient-to-r from-[#0F2040]/5 to-transparent border border-[#0F2040]/15 rounded-md p-2.5">
          <Sparkles className="w-4 h-4 text-[#0F2040] shrink-0" />
          <div className="flex-1 text-[11px] text-slate-700 leading-tight">
            Let AI draft a response grounded in peer precedent
          </div>
          <select value={tone} onChange={e => setTone(e.target.value)}
            disabled={drafting}
            className="text-[11px] border border-slate-300 rounded px-1.5 py-1 bg-white"
            data-testid="cr-tone-select">
            {TONES.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <Button type="button" size="sm" variant="outline" onClick={() => handleAutoDraft()}
            disabled={drafting || submitting}
            className="h-7 text-[11px] border-[#0F2040]/30"
            data-testid="cr-autodraft-btn">
            {drafting
              ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Drafting…</>
              : <><Sparkles className="w-3 h-3 mr-1" />Auto-draft</>}
          </Button>
        </div>

        {(styleCount > 0 || firmCount > 0) && (
          <div className="text-[10px] text-slate-500 flex items-center gap-2 -mt-1 pl-2 flex-wrap"
               data-testid="cr-style-memory">
            {firmCount > 0 && (
              <span className="flex items-center gap-1" data-testid="cr-firm-guardrails">
                <span className="inline-block w-1 h-1 rounded-full bg-sky-500" />
                {firmCount} firm guardrail{firmCount === 1 ? '' : 's'} applied
              </span>
            )}
            {styleCount > 0 && (
              <span className="flex items-center gap-1">
                <span className="inline-block w-1 h-1 rounded-full bg-emerald-500" />
                <span title={styleCritiques.map(c => `• ${c}`).join('\n')}>
                  {styleCount} style note{styleCount === 1 ? '' : 's'} from your feedback
                </span>
                <button type="button" onClick={clearStyleMemory}
                  className="text-[10px] text-slate-400 hover:text-rose-600 underline decoration-dotted"
                  data-testid="cr-style-memory-clear">
                  clear
                </button>
              </span>
            )}
          </div>
        )}

        {rationale && (
          <div className="text-[11px] text-slate-600 bg-amber-50 border border-amber-200 rounded-md p-2 space-y-1.5"
               data-testid="cr-rationale">
            <div>
              <span className="font-medium text-amber-800">AI rationale: </span>
              {rationale}
              {peerCount > 0 && (
                <span className="text-slate-500"> · based on {peerCount} past accepted proposal{peerCount === 1 ? '' : 's'}</span>
              )}
            </div>

            {compliance && firmCount > 0 && (
              <div className="flex items-start gap-1.5 pt-1 border-t border-amber-200/60">
                {compliance.compliant ? (
                  <>
                    <ShieldCheck className="w-3 h-3 text-emerald-600 mt-0.5 shrink-0" />
                    <span className="text-[10px] text-emerald-700" data-testid="cr-compliance-ok">
                      Passes all {firmCount} firm guardrail{firmCount === 1 ? '' : 's'}
                      {autoRegenerated && ' (auto-regenerated once to fix violations)'}
                    </span>
                  </>
                ) : (
                  <>
                    <ShieldAlert className="w-3 h-3 text-rose-600 mt-0.5 shrink-0" />
                    <div className="text-[10px] text-rose-700" data-testid="cr-compliance-warn">
                      <span className="font-medium">Still violates {compliance.violations?.length || 0} guardrail{(compliance.violations?.length || 0) === 1 ? '' : 's'}:</span>
                      <ul className="list-disc ml-4 mt-0.5">
                        {(compliance.violations || []).map((v, i) => (
                          <li key={`${v.rule}-${i}`}><em>{v.rule}</em> — {v.reason}</li>
                        ))}
                      </ul>
                    </div>
                  </>
                )}
              </div>
            )}
            {draftId && (
              <div className="flex items-center gap-2 pt-1 border-t border-amber-200/60">
                {rating === null ? (
                  <>
                    <span className="text-[10px] text-slate-500">Rate this draft:</span>
                    <button type="button" onClick={() => sendRating(1)} disabled={savingRating}
                      className="p-1 rounded hover:bg-emerald-100 text-slate-500 hover:text-emerald-600 transition-colors disabled:opacity-40"
                      title="Helpful" data-testid="cr-rating-up">
                      <ThumbsUp className="w-3.5 h-3.5" />
                    </button>
                    <button type="button" onClick={() => setShowReason(true)} disabled={savingRating}
                      className="p-1 rounded hover:bg-rose-100 text-slate-500 hover:text-rose-600 transition-colors disabled:opacity-40"
                      title="Not helpful" data-testid="cr-rating-down">
                      <ThumbsDown className="w-3.5 h-3.5" />
                    </button>
                  </>
                ) : (
                  <div className="flex items-center gap-2 flex-wrap" data-testid="cr-rating-done">
                    <span className="flex items-center gap-1 text-[10px] text-slate-500">
                      <Check className="w-3 h-3 text-emerald-600" />
                      Feedback recorded {rating === 1 ? '(helpful)' : '(needs work)'}
                    </span>
                    {rating === -1 && draftId && (
                      <Button type="button" size="sm" variant="outline"
                        onClick={() => handleAutoDraft({ previousDraftId: draftId, critique: ratingReason })}
                        disabled={drafting}
                        className="h-6 text-[10px] px-2 border-[#0F2040]/30 text-[#0F2040]"
                        data-testid="cr-regenerate-btn">
                        {drafting
                          ? <><Loader2 className="w-3 h-3 mr-1 animate-spin" />Regenerating…</>
                          : <><RefreshCw className="w-3 h-3 mr-1" />Regenerate with this feedback</>}
                      </Button>
                    )}
                  </div>
                )}
              </div>
            )}
            {showReason && (
              <div className="flex items-center gap-1.5 pt-1">
                <input type="text" value={ratingReason} onChange={e => setRatingReason(e.target.value)}
                  placeholder="What was off? (optional)" maxLength={500}
                  className="flex-1 text-[11px] px-2 py-1 border border-rose-200 rounded bg-white"
                  data-testid="cr-rating-reason" />
                <Button type="button" size="sm" variant="outline"
                  onClick={() => sendRating(-1, ratingReason)} disabled={savingRating}
                  className="h-6 text-[10px] px-2 border-rose-300 text-rose-700"
                  data-testid="cr-rating-down-submit">
                  {savingRating ? <Loader2 className="w-3 h-3 animate-spin" /> : 'Submit'}
                </Button>
              </div>
            )}
          </div>
        )}

        <div className="space-y-3 mt-1">
          <div>
            <Label className="text-xs font-medium text-[#0F2040]">Proposed action</Label>
            <div className="mt-1.5 grid grid-cols-1 gap-1.5">
              {ACTIONS.map(a => {
                const Icon = a.icon;
                const active = action === a.value;
                return (
                  <button key={a.value} type="button" onClick={() => setAction(a.value)}
                    className={`text-left px-3 py-2 rounded-md border transition-colors flex items-start gap-2 ${active ? 'border-[#0F2040] bg-[#0F2040]/5' : 'border-slate-200 hover:bg-slate-50'}`}
                    data-testid={`cr-action-${a.value}`}>
                    <Icon className={`w-4 h-4 mt-0.5 ${active ? 'text-[#0F2040]' : 'text-slate-500'}`} />
                    <div className="flex-1">
                      <div className={`text-xs font-medium ${active ? 'text-[#0F2040]' : 'text-slate-700'}`}>{a.label}</div>
                      <div className="text-[10px] text-slate-500">{a.hint}</div>
                    </div>
                  </button>
                );
              })}
            </div>
          </div>

          <div>
            <Label className="text-xs font-medium text-[#0F2040]">Your response to the stakeholder</Label>
            <Textarea value={note} onChange={e => setNote(e.target.value)} rows={4}
              maxLength={3000} className="text-xs mt-1"
              placeholder="e.g. Happy to remove Hypercare in the initial package; we can keep it as a priced option for renewal."
              data-testid="cr-proposed-note" />
          </div>

          <label className="flex items-start gap-2 cursor-pointer">
            <input type="checkbox" checked={sendEmail} onChange={e => setSendEmail(e.target.checked)}
              disabled={!row.stakeholderEmail}
              className="mt-0.5" data-testid="cr-send-email" />
            <div>
              <div className="text-[11px] text-slate-700">
                Email {row.stakeholderName || row.stakeholderEmail || 'the stakeholder'} now
              </div>
              {!row.stakeholderEmail && (
                <div className="text-[10px] text-amber-600">No email on file — this will save as a draft.</div>
              )}
            </div>
          </label>
        </div>

        <DialogFooter>
          <Button variant="outline" size="sm" onClick={() => onOpenChange(false)}
            data-testid="cr-cancel-btn">Cancel</Button>
          <Button size="sm" onClick={handleSubmit} disabled={submitting}
            className="bg-[#0F2040] hover:bg-[#1A3055] text-white"
            data-testid="cr-submit-btn">
            {submitting ? <Loader2 className="w-3.5 h-3.5 mr-1.5 animate-spin" />
                        : <Send className="w-3.5 h-3.5 mr-1.5" />}
            {sendEmail && row.stakeholderEmail ? 'Send & create CR' : 'Create CR (draft)'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
